import React, { useEffect, useState } from 'react';
import { Reorder } from 'framer-motion';
import clsx from 'clsx';
import { ChevronUpIcon, ChevronDownIcon, Bars2Icon } from '@heroicons/react/24/outline';
import type { SurveyQuestion, RankedAnswer } from '@/types/survey';

interface Props {
  question: SurveyQuestion;
  // Explicitly allow undefined to satisfy exactOptionalPropertyTypes
  answer: RankedAnswer | undefined;
  onChange: (ans: RankedAnswer) => void;
}

export const RankedQuestion: React.FC<Props> = ({ question, answer, onChange }) => {
  // Local state for the list order
  const [items, setItems] = useState<string[]>([]);

  // Initialize state and handle Auto-Answer for default order
  useEffect(() => {
    const defaultOrder = question.options?.map((opt) => opt.id) ?? [];
    const initialOrder =
      answer?.ranking && answer.ranking.length > 0 ? answer.ranking : defaultOrder;

    // Only update local state if empty and we have an initial order
    if (items.length === 0 && initialOrder.length > 0) {
      setItems(initialOrder);
    }

    // If no answer yet, auto-submit the default order so "Next" works immediately
    if (!answer && initialOrder.length > 0) {
      onChange({
        question_id: question.id,
        question_type: 'ranked',
        ranking: initialOrder,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id, question.options]);

  // Handle Drag & Drop updates
  const handleReorder = (newOrder: string[]) => {
    setItems(newOrder);
    onChange({
      question_id: question.id,
      question_type: 'ranked',
      ranking: newOrder,
    });
  };

  // Handle Manual Arrow clicks (Accessibility/Fallback)
  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newOrder = [...items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newOrder.length) return;

    const curr = newOrder[index];
    const next = newOrder[targetIndex];
    if (!curr || !next) return;
    newOrder[index] = next;
    newOrder[targetIndex] = curr;
    handleReorder(newOrder);
  };

  if (!question.options || question.options.length === 0) {
    return <div className='text-gray-600'>No options available.</div>;
  }

  return (
    <div className='flex flex-col'>
      {/* Drag & Drop via Framer Motion */}
      <Reorder.Group
        axis='y'
        values={items}
        onReorder={handleReorder}
        className='flex flex-col gap-3'
      >
        {items.map((optId, index) => {
          const option = question.options?.find((opt) => opt.id === optId);
          if (!option) return null;

          return (
            <Reorder.Item
              key={optId}
              value={optId}
              className={clsx(
                'flex w-full items-center gap-3',
                'rounded-12 border border-gray-200 bg-white p-3 shadow-sm',
                'cursor-grab hover:border-brand-300 active:cursor-grabbing'
              )}
              whileDrag={{ scale: 1.02, boxShadow: '0px 10px 20px rgba(0,0,0,0.1)' }}
            >
              {/* Drag Handle Icon */}
              <div className='hidden shrink-0 text-gray-300 sm:block'>
                <Bars2Icon className='size-5' />
              </div>

              {/* Rank Number */}
              <div className='flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-14 font-bold text-brand-700'>
                {index + 1}
              </div>

              {/* Content */}
              <div className='flex flex-1 select-none items-center gap-3'>
                {option.icon && <span className='text-xl'>{option.icon}</span>}
                <span className='font-sans text-[15px] font-medium text-gray-800'>
                  {option.text}
                </span>
              </div>

              {/* Controls with explicit dark colors */}
              <div className='flex flex-col gap-1'>
                <button
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation();
                    moveItem(index, 'up');
                  }}
                  disabled={index === 0}
                  className='rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-brand-800 disabled:opacity-30'
                  aria-label='Move up'
                >
                  <ChevronUpIcon className='size-4 stroke-2' />
                </button>
                <button
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation();
                    moveItem(index, 'down');
                  }}
                  disabled={index === items.length - 1}
                  className='rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-brand-800 disabled:opacity-30'
                  aria-label='Move down'
                >
                  <ChevronDownIcon className='size-4 stroke-2' />
                </button>
              </div>
            </Reorder.Item>
          );
        })}
      </Reorder.Group>

      <p className='mt-4 text-center text-12 text-gray-300 sm:hidden'>Tap arrows to reorder</p>
    </div>
  );
};

export default RankedQuestion;
